import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDto } from 'src/app/models/device.model';
import { humanizeEnumValue } from 'src/app/utils/enum-display';
import {
  DashboardServiceDoneItem,
  ServiceDuePart,
  UAV,
} from '../../../models/report.model';
import {
  ColumnDefinition,
  DataColumnDefinition,
} from '../../qs-table/column-definition.interface';

type DashboardServiceDoneTableRow = {
  rank: number;
  device: DeviceDto;
  uav: UAV;
  partId: string;
  partType: string;
  partSubType: string;
  eventType: string;
  maintainer: string;
  timestampValue: number;
  timestampLabel: string;
};

@Component({
  selector: 'app-dashboard-service-done-list',
  templateUrl: './dashboard-service-done-list.component.html',
  styleUrls: ['./dashboard-service-done-list.component.scss'],
  standalone: false,
})
export class DashboardServiceDoneListComponent implements OnInit, OnChanges {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() items: DashboardServiceDoneItem[] = [];
  @Input() accent = '#0284c7';

  isCollapsed = false;
  tableRows: DashboardServiceDoneTableRow[] = [];
  columns: ColumnDefinition<DashboardServiceDoneTableRow>[] = [];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.columns = this.buildColumns();
    this.rebuildRows();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns = this.buildColumns();
        this.rebuildRows();
      });
  }

  ngOnChanges(): void {
    this.rebuildRows();
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  private buildColumns(): ColumnDefinition<DashboardServiceDoneTableRow>[] {
    return [
      {
        type: 'data',
        field: 'rank',
        header: '#',
        width: '70px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => (a.rank - b.rank) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'uav',
        header: this.translate.instant('dashboard.table.uav'),
        width: '240px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) =>
          this.formatUavLabel(a.uav).localeCompare(this.formatUavLabel(b.uav)) *
          multiplier,
        format: (row?: DashboardServiceDoneTableRow): string =>
          this.formatUavLabel(row?.uav ?? {}),
        tooltip: (row: DashboardServiceDoneTableRow): string =>
          this.formatUavTooltip(row.uav),
        onClick: (row: DashboardServiceDoneTableRow): void => {
          this.navigateToDroneDetails(row.device);
        },
        style: (row: DashboardServiceDoneTableRow) => ({
          'font-weight': '600',
          cursor: this.hasDeviceId(row.device) ? 'pointer' : 'default',
        }),
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'partId',
        header: this.translate.instant('dashboard.table.part_id'),
        width: '170px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => a.partId.localeCompare(b.partId) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'partType',
        header: this.translate.instant('dashboard.table.part_type'),
        width: '280px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => a.partType.localeCompare(b.partType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'partSubType',
        header: this.translate.instant('dashboard.table.part_sub_type'),
        width: '320px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => a.partSubType.localeCompare(b.partSubType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'eventType',
        header: this.translate.instant('dashboard.table.maintenance_event'),
        width: '360px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => a.eventType.localeCompare(b.eventType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'maintainer',
        header: this.translate.instant('dashboard.table.maintainer'),
        width: '180px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => a.maintainer.localeCompare(b.maintainer) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
      {
        type: 'data',
        field: 'timestampLabel',
        header: this.translate.instant('dashboard.table.timestamp'),
        width: '220px',
        sortFn: (
          a: DashboardServiceDoneTableRow,
          b: DashboardServiceDoneTableRow,
          multiplier: number,
        ) => (a.timestampValue - b.timestampValue) * multiplier,
      } as DataColumnDefinition<DashboardServiceDoneTableRow>,
    ];
  }

  private rebuildRows(): void {
    const topItems = this.items.slice(0, 10);

    this.tableRows = topItems.map((item, index) => {
      const timestampValue = this.parseTimestamp(item.timestamp);

      return {
        rank: index + 1,
        device: item.device,
        uav: item.uav,
        partId: this.formatPartId(item.partId),
        partType: humanizeEnumValue(item.partId.partType, '-'),
        partSubType: humanizeEnumValue(item.partId.partSubType, '-'),
        eventType: humanizeEnumValue(item.typeOfMaintenanceEvent, '-'),
        maintainer: item.maintainer || '-',
        timestampValue,
        timestampLabel: this.formatTimestamp(item.timestamp),
      };
    });
  }

  private formatPartId(part: ServiceDuePart): string {
    return part.id || '-';
  }

  private parseTimestamp(timestamp: string): number {
    if (!timestamp) {
      return 0;
    }

    const parsed = Date.parse(timestamp);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatTimestamp(timestamp: string): string {
    if (!timestamp) {
      return '-';
    }

    const parsed = this.parseTimestamp(timestamp);
    if (parsed <= 0) {
      return timestamp;
    }

    return formatDate(
      parsed,
      'short',
      this.translate.currentLang || this.translate.getDefaultLang() || 'en',
    );
  }

  // The cell already shows unit and model, so the tooltip adds the operator-assigned name when
  // there is one. Falling back to an empty string leaves the table's default tooltip in place.
  private formatUavTooltip(uav: UAV): string {
    return uav.nameAlias ?? '';
  }

  private formatUavLabel(uav: UAV): string {
    const hardwareRevision = this.toFiniteNumber(uav['hardwareRevision']);
    const unitId = this.toFiniteNumber(uav['unitId']);
    // Reports are always queried with resolveDevices=true, so the model name comes from the device
    // the backend resolved. There is deliberately no local hardware-revision lookup table here: it
    // would be a second, drifting copy of the backend model registry.
    const modelName = uav.modelName;

    if (unitId !== null && modelName) {
      return `${unitId} (${modelName})`;
    }

    if (unitId !== null && hardwareRevision !== null) {
      return `${unitId} (HR ${hardwareRevision})`;
    }

    if (unitId !== null) {
      return `${unitId}`;
    }

    if (modelName) {
      return modelName;
    }

    if (hardwareRevision !== null) {
      return `HR ${hardwareRevision}`;
    }

    return Object.keys(uav).length > 0
      ? JSON.stringify(uav)
      : this.translate.instant('dashboard.labels.unknown_uav');
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private navigateToDroneDetails(device: DeviceDto): void {
    if (!this.hasDeviceId(device)) {
      return;
    }

    this.router.navigateByUrl(`/device/${device.deviceId}`);
  }

  private hasDeviceId(device: DeviceDto | undefined): boolean {
    return (
      typeof device?.deviceId === 'string' && device.deviceId.trim().length > 0
    );
  }
}
